"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type Gender = "male" | "female"
export type MaritalStatus = "single" | "married" | "divorced" | "widow"

export interface FamilyMember {
  type: "spouse" | "daughter" | "son"
  fullName: string
  dob: string
  gender?: Gender
  maritalStatus?: string
  visaEmirate?: string
  height: string
  weight: string
}

export interface UserInfoData {
  /* Step 1 — Personal info */
  fullName: string
  email: string
  mobile: string
  nationality: string
  monthlyIncome: string
  gender: Gender | ""
  maritalStatus: MaritalStatus | ""
  dob: number // age in years

  /* Step 3 — Family members */
  familyMembers: FamilyMember[]

  completedAt: string | null
}

/* ------------------------------------------------------------------ */
/*  Store                                                              */
/* ------------------------------------------------------------------ */

interface UserInfoState extends UserInfoData {
  setContactInfo: (data: {
    fullName: string
    email: string
    mobile: string
    nationality: string
    monthlyIncome: string
  }) => void
  setPersonalDetails: (
    gender: Gender,
    maritalStatus: MaritalStatus,
    dob: number
  ) => void
  setFamilyMembers: (members: FamilyMember[]) => void
  markCompleted: () => void
  isCompleted: () => boolean
  reset: () => void
}

const defaults: UserInfoData = {
  fullName: "",
  email: "",
  mobile: "",
  nationality: "",
  monthlyIncome: "",
  gender: "",
  maritalStatus: "",
  dob: 0,
  familyMembers: [],
  completedAt: null,
}

export const useUserInfoStore = create<UserInfoState>()(
  persist(
    (set, get) => ({
      ...defaults,

      setContactInfo: (data) => set(data),

      setPersonalDetails: (gender, maritalStatus, dob) =>
        set({ gender, maritalStatus, dob }),

      setFamilyMembers: (members) => set({ familyMembers: members }),

      markCompleted: () => set({ completedAt: new Date().toISOString() }),

      isCompleted: () => get().completedAt !== null,

      reset: () => set({ ...defaults }),
    }),
    {
      name: "nova_user_info",
      partialize: (state) => ({
        fullName: state.fullName,
        email: state.email,
        mobile: state.mobile,
        nationality: state.nationality,
        monthlyIncome: state.monthlyIncome,
        gender: state.gender,
        maritalStatus: state.maritalStatus,
        dob: state.dob,
        familyMembers: state.familyMembers,
        completedAt: state.completedAt,
      }),
    }
  )
)
